import { Box, Stack } from "@mui/material";
import { FoodsIconButton, FoodsTooltip, ScoreValue, Typography } from "@foodpilot/foods";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/context/LanguageProvider";
import { MainScore as MainScoreType } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ScoreImprovement } from "src/types";
import { formatNumber } from "src/utils/formatting";
import { formatGramsForDisplay } from "../ecobalyseMass";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";
import { ScoreLoadingSkeleton } from "src/components/Score/ScoreLoadingSkeleton";

type ProductWeightSummary = {
    captionWeightG: number | null;
    unit: string | null;
};

type TotalScoreCardProps = {
    mainScore: MainScoreType | undefined;
    unit: string | undefined;
    precision: number;
    referenceScore: number | undefined;
    referenceName: string | undefined;
    improvement: ScoreImprovement;
    weight?: ProductWeightSummary;
    isLoading?: boolean;
    scoreQueryError?: unknown;
    retryScoreQuery?: () => Promise<unknown> | void;
    emptyGuidance?: ReactNode;
};

const getComparisonDetails = (
    scoreValue: number,
    referenceScore: number,
    improvement: ScoreImprovement,
    language: string,
) => {
    if (referenceScore === 0) {
        return null;
    }

    const relativeDifference = (scoreValue - referenceScore) / referenceScore;
    const direction =
        relativeDifference > 0 ? "+"
        : relativeDifference < 0 ? "-"
        : "=";
    const difference = new Intl.NumberFormat(language, {
        style: "percent",
        maximumFractionDigits: 0,
    }).format(Math.abs(relativeDifference));
    const isImproved =
        relativeDifference === 0 ? null
        : improvement === "decrease" ? relativeDifference < 0
        : relativeDifference > 0;

    return { difference, direction, isImproved };
};

/** Product score value and reference comparison from Figma empty `12819:67604` and filled `13154:221913`. */
export const TotalScoreCard = (props: TotalScoreCardProps) => {
    const {
        mainScore,
        unit,
        precision,
        referenceScore,
        referenceName,
        improvement,
        weight,
        isLoading = false,
        scoreQueryError,
        retryScoreQuery,
        emptyGuidance,
    } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();

    const scoreValue = OlapScoresService.normalizeNumericalScore(mainScore?.score.value, null);
    const isScoreEmpty = scoreValue === null;
    const hasScoreQueryError = scoreQueryError !== null && scoreQueryError !== undefined;

    const formattedScore =
        isScoreEmpty ? "" : (
            formatNumber(scoreValue, currentLanguage, {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
            })
        );

    const captionWeightG = weight?.captionWeightG ?? null;
    const weightUnit = weight?.unit ?? null;
    const hasWeightCaption = captionWeightG !== null && weightUnit !== null;
    const formattedWeight =
        hasWeightCaption ?
            formatNumber(formatGramsForDisplay(captionWeightG), currentLanguage, {
                maximumFractionDigits: 0,
            })
        :   null;

    const comparisonDetails =
        !isScoreEmpty && referenceScore !== undefined ?
            getComparisonDetails(scoreValue, referenceScore, improvement, currentLanguage)
        :   null;
    const referenceDisplayName =
        referenceName ?? t("product.v2.score_live.total.reference_label", "Agribalyse reference");
    const referenceTooltip = t(
        "product.v2.score_live.total.selected_reference_tooltip",
        'Reference score from the reference product you selected ("{{name}}"), sourced from {{source}}.',
        { name: referenceDisplayName, source: "Agribalyse" },
    );
    const comparisonInfoLabel = t("product.v2.score_live.total.reference_info", "About the reference score");
    const renderedEmptyGuidance =
        emptyGuidance === undefined ?
            t(
                "product.v2.score_live.total.empty_guidance",
                "Complete the recipe and other information about your product to achieve a score.",
            )
        :   emptyGuidance;

    return (
        <Box
            aria-busy={isLoading}
            data-testid="total-score-card"
            sx={(theme) => ({
                backgroundColor: theme.palette.primary[900],
                borderRadius: theme.spacing(1.5),
                boxShadow: theme.custom.shadows.grey,
                color: theme.custom.grey[100],
                overflow: "hidden",
                padding: theme.spacing(2),
                transition: theme.custom.scoreCard.loading.transition,
            })}
        >
            {isLoading ?
                <ScoreLoadingSkeleton tone="inverse" />
            : hasScoreQueryError ?
                <ScoreQueryError
                    context="details"
                    onRetry={retryScoreQuery}
                    status={getRemoteErrorStatus(scoreQueryError)}
                />
            :   <Stack sx={(theme) => ({ gap: theme.spacing(2) })}>
                    {isScoreEmpty ?
                        <ScoreValue
                            value={null}
                            unit={unit ?? ""}
                            emptyLabel={t("product.v2.score_live.total.empty_label", "Score unavailable")}
                        />
                    :   <Stack
                            direction="row"
                            alignItems="flex-end"
                            flexWrap="wrap"
                            sx={(theme) => ({ columnGap: theme.spacing(1), rowGap: theme.spacing(0.5) })}
                        >
                            <ScoreValue value={formattedScore} unit={unit ?? ""} />
                            {formattedWeight !== null && weightUnit !== null && (
                                <Stack
                                    direction="row"
                                    alignItems="flex-end"
                                    sx={(theme) => ({
                                        color: theme.custom.grey[100],
                                        gap: theme.spacing(1),
                                        paddingBottom: theme.spacing(0.25),
                                        whiteSpace: "nowrap",
                                    })}
                                >
                                    <Typography variant="small-regular" sx={{ color: "inherit" }}>
                                        {t("for", "for")}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="flex-end"
                                        sx={(theme) => ({ gap: theme.spacing(0.5) })}
                                    >
                                        <Typography variant="number-small" sx={{ color: "inherit" }}>
                                            {formattedWeight}
                                        </Typography>
                                        <Typography variant="body-regular" sx={{ color: "inherit" }}>
                                            {weightUnit}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    }

                    {isScoreEmpty && renderedEmptyGuidance !== null && (
                        <Typography variant="small-medium" sx={(theme) => ({ color: theme.palette.primary[50] })}>
                            {renderedEmptyGuidance}
                        </Typography>
                    )}

                    {!isScoreEmpty && referenceScore !== undefined && (
                        <Box
                            sx={(theme) => ({
                                alignItems: "center",
                                backgroundColor:
                                    comparisonDetails?.isImproved === true ? theme.custom.status.green.surface
                                    : comparisonDetails?.isImproved === false ? theme.custom.red[50]
                                    : theme.custom.grey[300],
                                borderRadius: theme.spacing(1.5),
                                boxSizing: "border-box",
                                display: "grid",
                                gap: theme.spacing(0.5),
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                minHeight: theme.spacing(10.375),
                                minWidth: 0,
                                padding: theme.spacing(1),
                                width: "100%",
                            })}
                        >
                            <Stack sx={(theme) => ({ flexGrow: 1, gap: theme.spacing(0.5), minWidth: 0 })}>
                                {comparisonDetails !== null && (
                                    <Typography
                                        variant="body-medium"
                                        sx={(theme) => ({
                                            color:
                                                comparisonDetails.isImproved === null ? theme.custom.grey[2800]
                                                : comparisonDetails.isImproved ? theme.custom.green[800]
                                                : theme.custom.red[800],
                                            lineHeight: theme.spacing(2.8),
                                            overflowWrap: "anywhere",
                                        })}
                                    >
                                        {t(
                                            "product.v2.score_live.total.reference_comparison_summary",
                                            "{{direction}}{{difference}} compared to the reference",
                                            {
                                                direction: comparisonDetails.direction,
                                                difference: comparisonDetails.difference,
                                            },
                                        )}
                                    </Typography>
                                )}
                                <Typography
                                    variant="small-regular"
                                    sx={(theme) => ({
                                        color: theme.custom.grey[2500],
                                        lineHeight: theme.spacing(2.4),
                                        overflowWrap: "anywhere",
                                    })}
                                >
                                    {t(
                                        "product.v2.score_live.total.reference_score",
                                        "Reference score: {{score}} {{unit}}",
                                        {
                                            score: formatNumber(referenceScore, currentLanguage, {
                                                maximumFractionDigits: precision,
                                            }),
                                            unit: unit ?? "",
                                        },
                                    )}
                                </Typography>
                            </Stack>
                            <FoodsTooltip title={referenceTooltip} variant="simple">
                                <Box component="span" sx={{ display: "inline-flex" }}>
                                    <FoodsIconButton
                                        size="s"
                                        icon="information"
                                        buttonProps={{
                                            "aria-label": comparisonInfoLabel,
                                            sx: (theme) => ({
                                                color:
                                                    comparisonDetails?.isImproved === true ? theme.custom.green[800]
                                                    : comparisonDetails?.isImproved === false ? theme.custom.red[800]
                                                    : theme.custom.grey[2500],
                                                flexShrink: 0,
                                                height: theme.spacing(2.5),
                                                minWidth: theme.spacing(2.5),
                                                padding: 0,
                                                width: theme.spacing(2.5),
                                            }),
                                        }}
                                    />
                                </Box>
                            </FoodsTooltip>
                        </Box>
                    )}
                </Stack>
            }
        </Box>
    );
};
